import { MetaFunction, json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import imageBrandonMcAnsh from "~/assets/team/brandon-mcansh.jpg";
import { Border } from "~/components/border";
import { ContactSection } from "~/components/contact-section";
import { Container } from "~/components/container";
import { FadeIn, FadeInStagger } from "~/components/fade-in";
import { GridList, GridListItem } from "~/components/grid-list";
import { PageIntro } from "~/components/page-intro";
import { SectionIntro } from "~/components/section-intro";
import { StatList, StatListItem } from "~/components/stat-list";
import { clientsLight } from "~/lib/client.server";

function Culture() {
  return (
    <div className="mt-24 sm:mt-32">
      <SectionIntro
        eyebrow="How we work"
        title="Five habits that decide whether a release lands."
      >
        <p>
          None of this is unusual. It is just uncommon enough that teams notice
          the difference within a sprint.
        </p>
      </SectionIntro>
      <Container className="mt-12">
        <GridList>
          <GridListItem title="Ship weekly, not eventually">
            Something installable every week from week two. Progress you can
            hold in your hand beats a progress report.
          </GridListItem>
          <GridListItem title="Take the problem, not the ticket">
            If it stands between you and a release and it is within reach, we
            own it — build config, flaky tests, store review, whatever it is.
          </GridListItem>
          <GridListItem title="Say the hard thing early">
            A slipping date, a wrong approach, a feature not worth building —
            you hear it the day we see it, not in the retro.
          </GridListItem>
          <GridListItem title="Leave the codebase better">
            Docs, tests and boundaries your team can carry after we disengage.
            We build ourselves out of a job on purpose.
          </GridListItem>
          <GridListItem title="Stay close to the platform">
            SwiftUI, Compose, Kotlin Multiplatform and the release tooling
            around them. We read the release notes so you do not have to.
          </GridListItem>
        </GridList>
      </Container>
    </div>
  );
}

const team = [
  {
    title: "Team",
    people: [
      {
        name: "Brandon McAnsh",
        role: "Founder/Lead Developer",
        image: { src: imageBrandonMcAnsh },
      },
    ],
  },
];

function Team() {
  return (
    <Container className="mt-24 sm:mt-32">
      <div className="space-y-24">
        {team.map((group) => (
          <FadeInStagger key={group.title}>
            <Border as={FadeIn} />
            <div className="grid grid-cols-1 gap-6 pt-12 sm:pt-16 lg:grid-cols-4 xl:gap-8">
              <FadeIn>
                <h2 className="font-display text-2xl font-semibold text-ink-50">
                  {group.title}
                </h2>
              </FadeIn>
              <div className="lg:col-span-3">
                <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:gap-8">
                  {group.people.map((person) => (
                    <li key={person.name}>
                      <FadeIn>
                        <div className="group relative overflow-hidden rounded-2xl border border-ink-700 bg-ink-900">
                          <img
                            alt=""
                            {...person.image}
                            className="h-96 w-full object-cover grayscale transition duration-500 motion-safe:group-hover:scale-105"
                          />
                          <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black to-black/0 to-40% p-6">
                            <p className="font-display text-base/6 font-semibold tracking-wide text-white">
                              {person.name}
                            </p>
                            <p className="mt-2 text-sm text-white">
                              {person.role}
                            </p>
                          </div>
                        </div>
                      </FadeIn>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeInStagger>
        ))}
      </div>
    </Container>
  );
}

export async function loader() {
  let clients = clientsLight.length;
  return json({ clients });
}

export const meta: MetaFunction = () => {
  return [
    { title: "About Us" },
    {
      name: "description",
      content: `Senior mobile engineers measured by what ships. We take extreme ownership of a release, work in your codebase and your process, and hand back something your team can maintain.`,
    },
  ];
};

export default function About() {
  let data = useLoaderData<typeof loader>();

  return (
    <>
      <PageIntro eyebrow="About us" title="We are measured by what ships">
        <p>
          Extreme ownership of a release, in your codebase and your process. The
          proof is the imprint we leave on a team after we disengage.
        </p>
        <div className="mt-10 max-w-2xl space-y-6 text-base">
          <p>
            bmcreations started with one Android developer who kept seeing the
            same gap: teams that needed a senior engineer to step in, find the
            real blocker, and get the release out — fast, without a three-month
            ramp.
          </p>
          <p>
            That is still the job. Today most of it runs through Kotlin
            Multiplatform, because sharing the logic layer is the cheapest way
            we know to stop iOS and Android drifting into two different products
            on two different schedules. The UI stays native on both sides, so
            users never pay for the shortcut.
          </p>
          <p>
            We are not a body shop and we are not a pitch deck. You talk to the
            engineers who write the code, from the first call through store
            submission and the release after that.
          </p>
        </div>
      </PageIntro>
      <Container className="mt-14">
        <StatList>
          <StatListItem
            value={String(data.clients)}
            label="Product teams shipped for"
          />
          <StatListItem value="500,000+" label="People using what we built" />
          <StatListItem value="2" label="App Store features and awards" />
        </StatList>
      </Container>

      <Culture />

      <Team />

      <ContactSection />
    </>
  );
}
